import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent as AlertContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAgents } from '@/hooks/useAgents';
import { useNavigation } from '@/contexts/NavigationContext';
import { useScopeOptions } from '@/hooks/useScopeOptions';
import { Search, ExternalLink } from 'lucide-react';
import { getProjectIdFromFilter } from '@/types/navigation';

interface AgentSelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (agent: { id: string; name: string }) => void;
}

export const AgentSelectorModal = ({ open, onOpenChange, onSelect }: AgentSelectorModalProps) => {
  const {
    navigation,
    navigateToGlobal,
    navigateToPersonal,
    navigateToProject,
    navigateToTab,
    scopeFilter,
    setScopeFilter,
    activeConversationId,
    setActiveConversationId,
    setActiveAgentId,
    conversationHasMessages,
  } = useNavigation();
  const scopeOptions = useScopeOptions();

  const [search, setSearch] = useState('');
  const [pendingAgent, setPendingAgent] = useState<{ id: string; name: string } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleOpenChange = (val: boolean) => {
    if (val) {
      setSearch('');
    }
    onOpenChange(val);
  };

  const projectId = navigation.projectId;
  const { data: globalAgents = [] } = useAgents('global', undefined, projectId);
  const { data: personalAgents = [] } = useAgents('personal');

  const allAgents = useMemo(() => {
    const map = new Map<string, typeof globalAgents[0]>();
    globalAgents.forEach(a => map.set(a.id, a));
    personalAgents.forEach(a => map.set(a.id, a));
    return Array.from(map.values());
  }, [globalAgents, personalAgents]);

  const filtered = useMemo(() => {
    let list = [...allAgents];
    const filterProjectId = getProjectIdFromFilter(scopeFilter);

    if (filterProjectId) {
      list = list.filter(a => a.project_id === filterProjectId);
    } else if (scopeFilter === 'global') {
      list = list.filter(a => a.scope_type === 'global' && !a.project_id);
    } else if (scopeFilter === 'personal') {
      list = list.filter(a => a.scope_type === 'personal' && !a.project_id);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(a =>
        a.name.toLowerCase().includes(q) ||
        a.description?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [allAgents, scopeFilter, search]);

  const applyAgentSelection = (agent: { id: string; name: string }, agentData?: typeof globalAgents[0]) => {
    if (agentData) {
      if (agentData.project_id) {
        navigateToProject(agentData.project_id);
      } else if (agentData.scope_type === 'personal' && !navigation.isPersonal) {
        navigateToPersonal();
      } else if (agentData.scope_type === 'global' && navigation.isPersonal) {
        navigateToGlobal();
      }
    }
    onSelect(agent);
    onOpenChange(false);
  };

  const handleAgentClick = (agent: typeof globalAgents[0]) => {
    const selection = { id: agent.id, name: agent.name };

    if (activeConversationId && conversationHasMessages) {
      setPendingAgent(selection);
      setConfirmOpen(true);
    } else {
      applyAgentSelection(selection, agent);
    }
  };

  const confirmNewConversation = () => {
    if (pendingAgent) {
      const agentData = allAgents.find(a => a.id === pendingAgent.id);
      if (agentData) {
        if (agentData.project_id) {
          navigateToProject(agentData.project_id);
        } else if (agentData.scope_type === 'personal') {
          navigateToPersonal();
        } else if (agentData.scope_type === 'global') {
          navigateToGlobal();
        }
      }
      setActiveConversationId(null);
      setActiveAgentId(pendingAgent.id);
      onSelect(pendingAgent);
      onOpenChange(false);
    }
    setPendingAgent(null);
    setConfirmOpen(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <DialogTitle>Selecionar Agente</DialogTitle>
              <button
                onClick={() => {
                  const filterProjectId = getProjectIdFromFilter(scopeFilter);
                  if (filterProjectId) {
                    navigateToProject(filterProjectId);
                  } else if (scopeFilter === 'personal') {
                    navigateToPersonal();
                  } else {
                    navigateToGlobal();
                  }
                  navigateToTab('agents');
                  onOpenChange(false);
                }}
                className="flex items-center gap-1 text-xs text-tertiary hover:text-primary transition-colors"
                title="Abrir biblioteca de agentes"
              >
                <ExternalLink className="w-3 h-3" />
                Ver biblioteca
              </button>
            </div>
          </DialogHeader>

          <div className="flex items-center gap-2 flex-nowrap">
            {scopeOptions.map(s => (
              <button
                key={s.id}
                onClick={() => setScopeFilter(s.id)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 whitespace-nowrap ${
                  scopeFilter === s.id
                    ? 'bg-brand-primary text-white border-transparent'
                    : 'border-color text-secondary hover:bg-hover'
                }`}
              >
                {s.icon}{s.label}
              </button>
            ))}
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary" />
            <input
              type="text"
              placeholder="Buscar agentes..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full h-9 pl-10 pr-4 rounded-md border border-color bg-surface text-sm text-primary placeholder:text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-primary"
            />
          </div>

          <div className="flex-1 overflow-y-auto min-h-0 space-y-1">
            {filtered.length === 0 ? (
              <p className="text-sm text-tertiary text-center py-8">Nenhum agente encontrado</p>
            ) : (
              filtered.map(agent => (
                <button
                  key={agent.id}
                  onClick={() => handleAgentClick(agent)}
                  className="w-full text-left px-3 py-2.5 rounded-md hover:bg-hover transition-colors"
                >
                  <span className="text-sm font-medium text-primary">{agent.name}</span>
                  {agent.description && (
                    <p className="text-xs text-tertiary mt-0.5 line-clamp-2">{agent.description}</p>
                  )}
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Deseja iniciar uma nova conversa com o agente {pendingAgent?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              A substituição de agentes requer o início de uma nova conversa. A conversa atual será preservada no histórico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingAgent(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmNewConversation}>Nova conversa</AlertDialogAction>
          </AlertDialogFooter>
        </AlertContent>
      </AlertDialog>
    </>
  );
};
